/**
 * A lot of this is currently unused code.
 * The idea is to migrate all the config functions to here.
 * See the POC PR here - https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/158198/overview
 * But, due to FGing, risk, etc, we won't do it in one go.
 * So the config will be a mix of from here, vs elsewhere for a short period of time.
 */

import FeatureGates from '@atlaskit/feature-gate-js-client';
import React from 'react';
import { MessageDescriptor } from 'react-intl';

import { LoadingState } from '@townsquare/loading-state';
import { TEAMS_AS_A_STANDALONE_APP } from '@townsquare/stat-sig/experiments';

import { PeopleFieldType } from '../components/__generated__/StaffDirectoryViewQuery.graphql';

const ScreenTypes = [
  'BROWSE_ALL',
  'SEARCH_ALL',
  'SEARCH_PEOPLE',
  'SEARCH_TEAMS',
  'YOUR_TEAMS',
  'BROWSE_DEPARTMENTS',
  'BROWSE_LOCATIONS',
  'BROWSE_JOB_TITLES',
  'BROWSE_KUDOS',
  'BROWSE_AGENTS',
] as const;
export type ScreenType = (typeof ScreenTypes)[number];

type ScreenConfig = {
  searchBarEnabled: boolean;
  type: 'TEAM' | 'PEOPLE' | 'KUDOS' | 'LANDING';
  filterRestriction?: 'people' | 'teams' | 'kudos';
  pageTitle?: (intl: (message: MessageDescriptor) => string) => string;
  peopleFieldType?: PeopleFieldType;
  showRecentInSearch?: boolean;
  searchBarPreviewTQL?: (tql: any) => any;
  showCreateTeamButton?: boolean;
  headerEnabled?: boolean;
  showInviteUserButton?: (canInviteUserToSite: boolean) => boolean;
  fetchCanInviteUserToSite?: boolean;
  Skeleton?: React.ReactNode;
};

type ScreenFeature = keyof ScreenConfig;

// Some values need fallbacks to avoid needing to do fallbacks in place
type ScreenConfigWithDefaults = ScreenConfig &
  Required<
    Pick<
      ScreenConfig,
      | 'peopleFieldType'
      | 'searchBarPreviewTQL'
      | 'headerEnabled'
      | 'showInviteUserButton'
      | 'fetchCanInviteUserToSite'
      | 'Skeleton'
      | 'pageTitle'
    >
  >;

const defaultScreenConfig: ScreenConfigWithDefaults = {
  searchBarEnabled: false,
  type: 'LANDING',
  filterRestriction: undefined,
  pageTitle: () => '',
  peopleFieldType: 'DEPARTMENT',
  searchBarPreviewTQL: () => undefined,
  headerEnabled: true,
  showInviteUserButton: canInviteUserToSite => canInviteUserToSite,
  fetchCanInviteUserToSite: true,
  Skeleton: null, // TODO: before enabling this should be BrowseAllSkeleton, but it is causing cyclic dependency issues
};

export const screenConfig: Record<ScreenType, ScreenConfig> = {
  BROWSE_ALL: {
    searchBarEnabled: false,
    type: 'LANDING',
    showRecentInSearch: true,
  },
  SEARCH_ALL: {
    searchBarEnabled: true,
    type: 'LANDING',
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.search-all',
        description: 'Search all screen',
        defaultMessage: 'People and teams',
      }),
    Skeleton: <LoadingState />,
  },
  SEARCH_PEOPLE: {
    searchBarEnabled: true,
    type: 'PEOPLE',
    filterRestriction: 'people',
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.search-people',
        description: 'Search people screen',
        defaultMessage: 'People',
      }),
    showRecentInSearch: true,
    searchBarPreviewTQL: tql => tql,
    headerEnabled:
      FeatureGates.initializeCompleted() &&
      !FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false),
    Skeleton: <LoadingState />,
  },
  SEARCH_TEAMS: {
    searchBarEnabled: true,
    type: 'TEAM',
    filterRestriction: 'teams',
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.search-teams',
        description: 'Search teams screen',
        defaultMessage: 'Teams',
      }),
    showCreateTeamButton: true,
    headerEnabled:
      FeatureGates.initializeCompleted() &&
      !FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false),
    showInviteUserButton: () => false,
    fetchCanInviteUserToSite: false,
    Skeleton: <LoadingState />,
  },
  YOUR_TEAMS: {
    searchBarEnabled: true,
    type: 'TEAM',
    filterRestriction: 'teams',
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.search-your-teams',
        description: 'Search your teams screen',
        defaultMessage: 'Teams',
      }),
    showCreateTeamButton: true,
    headerEnabled:
      FeatureGates.initializeCompleted() &&
      !FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false),
    showInviteUserButton: () => false,
    fetchCanInviteUserToSite: false,
    Skeleton: <LoadingState />,
  },
  BROWSE_KUDOS: {
    searchBarEnabled: false,
    type: 'KUDOS',
    filterRestriction: 'kudos',
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.browse-kudos',
        description: 'Browse kudos screen',
        defaultMessage: 'Kudos',
      }),
    searchBarPreviewTQL: tql => tql,
    headerEnabled:
      FeatureGates.initializeCompleted() &&
      !FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false),
    showInviteUserButton: () => false,
    fetchCanInviteUserToSite: false,
    Skeleton: <LoadingState />,
  },
  BROWSE_LOCATIONS: {
    searchBarEnabled: false,
    type: 'PEOPLE',
    peopleFieldType: 'LOCATION',
    showRecentInSearch: true,
    searchBarPreviewTQL: tql => tql,
    Skeleton: <LoadingState />,
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.browse-locations',
        description: 'Browse locations screen',
        defaultMessage: 'Locations',
      }),
  },
  BROWSE_DEPARTMENTS: {
    searchBarEnabled: false,
    type: 'PEOPLE',
    showRecentInSearch: true,
    searchBarPreviewTQL: tql => tql,
    Skeleton: <LoadingState />,
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.browse-departments',
        description: 'Browse departments screen',
        defaultMessage: 'Departments',
      }),
  },
  BROWSE_JOB_TITLES: {
    searchBarEnabled: false,
    type: 'PEOPLE',
    peopleFieldType: 'JOB_TITLE',
    showRecentInSearch: true,
    searchBarPreviewTQL: tql => tql,
    Skeleton: <LoadingState />,
    pageTitle: intl =>
      intl({
        id: 'townsquare.web.directory-header.browse-job-titles',
        description: 'Browse job titles screen',
        defaultMessage: 'Job titles',
      }),
  },
  BROWSE_AGENTS: {
    searchBarEnabled: false,
    type: 'PEOPLE',
  },
};

export const DEFAULT_SCREEN_TYPE: ScreenType = 'BROWSE_ALL';

export function configForScreen<T extends ScreenFeature>(screen: ScreenType, feature: T): ScreenConfigWithDefaults[T] {
  const configMergedWithDefaults = {
    ...defaultScreenConfig,
    ...screenConfig[screen],
  };
  return configMergedWithDefaults[feature];
}

export const getScreenFromStringOrDefault = (screenKey: string | undefined): ScreenType =>
  screenKey && ScreenTypes.includes(screenKey as ScreenType) ? (screenKey as ScreenType) : DEFAULT_SCREEN_TYPE;
